import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RADOM_COLORS } from '../util/Constants'
import Table from '../components/Table'
import Radom, { Deposit, ManagedEvent, Payout, Transaction, TransactionKind, TransactionStatus, WithdrawalRequest, WithdrawalRequestWithAccountName } from '../state/Radom'
import Dropdown, { DropdownItem } from '../components/Dropdown'
import { testNetworks, prodNetworks, getMethod } from '../util/Managed'
import { shortAddress, shouldRound, trimZeros, useTestMode } from '../util/Util'
import Pagination from '../components/Pagination'
import Checkbox from '../components/Checkbox'
import PayoutTransactionLogo from '../images/PayoutTransaction.svg'
import WithdrawalTransactionLogo from '../images/WithdrawalTransaction.svg'
import PaymentTransactionLogo from '../images/PaymentTransaction.svg'
import DepositTransactionLogo from '../images/DepositTransaction.svg'
import TimeText from '../components/TimeText'
import styled from 'styled-components'
import { AssociatedEntityType, ManagedPaymentEvent, parseManagedEvent } from '../state/Events'
import USDLogo from '../images/USDLogo.svg'
import EUROLogo from '../images/EUROLogo.svg'
import { Chevron } from '../icons/Chevron'
import ExportTransactionsForm from '../components/ExportTransactionsForm'
import Modal from '../components/Modal'
import Dots from '../icons/Dots'
import PageHeader from '../components/PageHeader'
import _ from 'lodash'

export interface TransactionSummary {
  in: {
    img: string | undefined
    amount: string | number
    ticker: string
  }
  out?: {
    img: string | undefined
    amount: string | number
    ticker: string
  }
}

export interface ExtendedTransaction extends Transaction {
  status: TransactionStatus
  summary: TransactionSummary
  related: string
  url: string
}

export const TransactionStatusBadge = styled.div<{ status: TransactionStatus }>`
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 14px;
  width: fit-content;

  ${({ status }) => {
    let bgColor = RADOM_COLORS.ERROR_LIGHT as string
    let textColor = RADOM_COLORS.ERROR as string

    switch (status) {
    case TransactionStatus.Pending:
      bgColor = RADOM_COLORS.GRAY_MED as string
      textColor = RADOM_COLORS.GRAY_DARKEST as string
      break
    case TransactionStatus.Successful:
      bgColor = RADOM_COLORS.SUCCESS_LIGHT as string
      textColor = RADOM_COLORS.SUCCESS as string
      break
    }

    return `
    background-color: ${bgColor};
    color: ${textColor};
  `
  }};
`

const TRANSACTION_ICONS = {
  [TransactionKind.Deposit]: DepositTransactionLogo,
  [TransactionKind.Withdrawal]: WithdrawalTransactionLogo,
  [TransactionKind.Payment]: PaymentTransactionLogo,
  [TransactionKind.Payout]: PayoutTransactionLogo
}

const Transactions = observer((): ReactElement => {
  const navigate = useNavigate()
  const [testMode] = useTestMode()

  const pageSize = 20
  const [curPage, setCurPage] = useState(0)

  const [isLoading, setIsLoading] = useState(false)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [extendedTransactions, setExtendedTransactions] = useState<ExtendedTransaction[]>([])
  const [exportTransactionsModalOpen, setExportTransactionsModalOpen] = useState(false)

  const [totalTransactions, setTotalTransactions] = useState<number>(0)

  const [transactionKinds, setTransactionKinds] = useState<TransactionKind[]>([
    TransactionKind.Deposit, TransactionKind.Payment, TransactionKind.Payout, TransactionKind.Withdrawal
  ])
  const [transactionStatuses, setTransactionStatuses] = useState<TransactionStatus[]>([
    TransactionStatus.Successful, TransactionStatus.Pending, TransactionStatus.Incomplete
  ])

  const fetchTransactions = async ({
    page,
    kinds,
    statuses,
    isTestMode
  }: {
    page: number
    kinds: TransactionKind[]
    statuses: TransactionStatus[]
    isTestMode: boolean
  }): Promise<void> => {
    setIsLoading(true)
    setTransactions([])

    Radom.listTransactions(
      page * pageSize,
      pageSize,
      kinds,
      statuses,
      isTestMode ? testNetworks : prodNetworks,
      new Date(0).toISOString(),
      new Date().toISOString(),
      isTestMode
    )
      .then(s => {
        setTransactions(s.data)
        setTotalTransactions(s.total)
      })
      .finally(() => setIsLoading(false))
  }

  const debouncedFetch = useMemo(() => _.debounce(fetchTransactions, 300), [])

  const getTransactionStatus = (transaction: Transaction): TransactionStatus => {
    const kind = transaction.kind
    switch (kind) {
    case TransactionKind.Withdrawal: {
      const withdrawal = transaction.payload as WithdrawalRequest
      if (withdrawal.withdrawalStatus === 'complete') {
        return TransactionStatus.Successful
      }
      return TransactionStatus.Pending
    }
    case TransactionKind.Payment: {
      const payment = transaction.payload as ManagedEvent
      if (payment.eventType === 'incompletePayment') {
        return TransactionStatus.Incomplete
      }
      return TransactionStatus.Successful
    }
    case TransactionKind.Payout: {
      const payout = transaction.payload as Payout
      if (payout.status === 'Complete') {
        return TransactionStatus.Successful
      }
      return TransactionStatus.Pending
    }
    }
    return TransactionStatus.Successful
  }

  const getUrl = (transaction: Transaction): string => {
    const kind = transaction.kind
    switch (kind) {
    case TransactionKind.Withdrawal: {
      return `/withdrawal/${transaction.id}?source=transactions`
    }
    case TransactionKind.Payment: {
      return `/payment/${transaction.id}?source=transactions`
    }
    case TransactionKind.Payout: {
      return `/payout/${transaction.id}?source=transactions`
    }
    }
    return `/deposit/${transaction.id}?source=transactions`
  }

  const getTransactionSummary = (transaction: Transaction): TransactionSummary => {
    const kind = transaction.kind
    switch (kind) {
    case TransactionKind.Withdrawal: {
      const withdrawal = transaction.payload as WithdrawalRequest
      const paymentMethod = getMethod(withdrawal.network, withdrawal.token)
      const isFiatWithdrawal = !!withdrawal.receipt?.FiatReceipt
      const fiat = withdrawal?.receipt?.FiatReceipt
      const finalAmount = fiat?.finalAmount
      const currency: 'EUR' | 'USD' = !fiat?.transferMethod || fiat.transferMethod.toLowerCase() === 'sepa' ? 'EUR' : 'USD'
      const logo = (currency === 'EUR' ? EUROLogo : USDLogo) as string

      return {
        in: {
          img: paymentMethod.logo,
          amount: withdrawal.withdrawalAmount,
          ticker: paymentMethod.ticker
        },
        ...(isFiatWithdrawal && finalAmount
          ? {
            out: {
              img: logo,
              amount: finalAmount,
              ticker: currency
            }
          }
          : {})
      }
    }
    case TransactionKind.Payment: {
      const payment = transaction.payload as ManagedEvent
      const managedPaymentEvent = parseManagedEvent(payment) as ManagedPaymentEvent
      return {
        in: {
          img: managedPaymentEvent.paymentMethod.logo,
          amount: managedPaymentEvent.amount,
          ticker: managedPaymentEvent.paymentMethod.ticker
        }
      }
    }
    case TransactionKind.Payout: {
      const payout = transaction.payload as Payout
      const paymentMethod = getMethod(
        payout.payouts[0].payoutType.crypto?.network || '',
        payout.payouts[0].payoutType.crypto?.token)
      return {
        in: {
          img: paymentMethod.logo,
          amount: payout.totalPayoutUsd,
          ticker: paymentMethod.ticker
        }
      }
    }
    }
    const deposit = transaction.payload as Deposit
    if (deposit.depositMethod.Crypto) {
      const paymentMethod = getMethod(
        deposit.depositMethod.Crypto.network,
        deposit.depositMethod.Crypto.token)

      return {
        in: {
          img: paymentMethod.logo,
          amount: deposit.netAmount,
          ticker: paymentMethod.ticker
        }
      }
    }
    const paymentMethodOut = getMethod(
      deposit.depositMethod.Fiat?.onrampNetwork || '',
      deposit.depositMethod.Fiat?.onrampToken)
    const paymentMethodIn = getMethod(
      'Fiat',
      deposit.depositMethod.Fiat?.fiatCurrency)
    return {
      in: {
        img: paymentMethodIn.logo,
        amount: deposit.fiatGrossAmount || '',
        ticker: paymentMethodIn.ticker
      },
      out: {
        img: paymentMethodOut.logo,
        amount: deposit.netAmount,
        ticker: paymentMethodOut.ticker
      }
    }
  }

  const getTransactionRelated = (transaction: Transaction): string => {
    const kind = transaction.kind
    switch (kind) {
    case TransactionKind.Withdrawal: {
      const withdrawal = transaction.payload as WithdrawalRequestWithAccountName
      return withdrawal.withdrawalAccountName ?? shortAddress(withdrawal.withdrawalAddress)
    }
    case TransactionKind.Payment: {
      const payment = transaction.payload as ManagedEvent
      const managedPaymentEvent = parseManagedEvent(payment) as ManagedPaymentEvent
      console.log(managedPaymentEvent)
      return AssociatedEntityType[managedPaymentEvent.associatedEntity.entityType]
    }
    case TransactionKind.Payout: {
      const payout = transaction.payload as Payout
      const tail = payout.payouts.length > 1 ? 'Recipients' : 'Receipient'
      return `${payout.payouts.length} ${tail}`
    }
    }
    const deposit = transaction.payload as Deposit

    return deposit.depositAccountName
  }

  useEffect(() => {
    debouncedFetch({
      page: curPage,
      kinds: transactionKinds,
      statuses: transactionStatuses,
      isTestMode: testMode
    })
    return () => debouncedFetch.cancel()
  }, [curPage, transactionKinds, transactionStatuses, testMode, debouncedFetch])

  useEffect(() => {
    setCurPage(0)
  }, [testMode, transactionKinds, transactionStatuses])

  useEffect(() => {
    setExtendedTransactions(
      transactions.map((item) =>
        ({
          ...item,
          status: getTransactionStatus(item),
          summary: getTransactionSummary(item),
          url: getUrl(item),
          related: getTransactionRelated(item)
        }))
    )
  }, [transactions])

  return <div style={{ display: 'flex', width: '100%', padding: '30px 48px', flexDirection: 'column', gap: '20px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <PageHeader title='Transactions' subTitle='Manage your transactions'/>
      <Dropdown
        noChevron
        selectedContent={<Dots width={15} />}
        dropdownContent={
          <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
            <DropdownItem onClick={() => setExportTransactionsModalOpen(true)}>Export transactions</DropdownItem>
          </div>
        }
      />
    </div>
    <div style={{ display: 'flex', gap: 10 }}>
      <Dropdown
        selectedContent={<div style={{ fontSize: '14px' }}>Status</div>}
        dropdownContent={
          <div>
            {Object.values(TransactionStatus).map((s) => (
              <DropdownItem
                key={s}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}
                onClick={() => {
                  if (isLoading) return
                  setTransactionStatuses((prev) =>
                    prev.includes(s)
                      ? prev.filter((x) => x !== s)
                      : [...prev, s])
                }}
              >
                <Checkbox size={16} checked={transactionStatuses.includes(s)} />
                <span>{s}</span>
              </DropdownItem>
            ))}
          </div>
        }
      />
      <Dropdown
        selectedContent={<div style={{ fontSize: '14px' }}>Type</div>}
        dropdownContent={
          <div>
            {Object.values(TransactionKind).map((k) => (
              <DropdownItem
                key={k}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}
                onClick={() => {
                  if (isLoading) return
                  setTransactionKinds((prev) =>
                    prev.includes(k)
                      ? prev.filter((x) => x !== k)
                      : [...prev, k]
                  )
                }}
              >
                <Checkbox size={16} checked={transactionKinds.includes(k)} />
                <span>{k}</span>
              </DropdownItem>
            ))}
          </div>
        }
      />
    </div>

    <Table
      borderless
      disableOnClickState
      headers={['Type', 'Status', 'Summary', 'Related', 'Date'].map(h => <span key={h}>{h}</span>)}
      onClickRow={i => {
        const s = extendedTransactions[i]
        navigate(s.url)
      }}
      isLoading={isLoading}
      items={extendedTransactions.map(s => {
        return [
          <div key={'1'} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <img src={TRANSACTION_ICONS[s.kind] as string} style={{ width: 20, height: 20 }} />
            <span>{s.kind}</span>
          </div>,
          <div key={'2'}>
            <TransactionStatusBadge status={s.status}>{s.status}</TransactionStatusBadge>
          </div>,
          <div key={'3'} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <img src={s.summary.in.img} style={{ width: 20, height: 20 }} />
            <span>
              {trimZeros(
                shouldRound(s.summary.in.ticker)
                  ? Number(s.summary.in.amount).toFixed(2)
                  : s.summary.in.amount.toString()
              )}
            </span>
            <span>{s.summary.in.ticker}</span>
            {
              s.summary.out && <>
                <Chevron style={{ opacity: 0.65, transform: 'rotate(-90deg)' }} />
                <img src={s.summary.out.img} style={{ width: 20, height: 20 }} />
                <span>
                  {trimZeros(
                    shouldRound(s.summary.out.ticker)
                      ? Number(s.summary.out.amount).toFixed(2)
                      : s.summary.out.amount.toString()
                  )}
                </span>
                <span>{s.summary.out.ticker}</span>
              </>
            }
          </div>,
          <div key={'4'}>
            <span>{s.related}</span>
          </div>,
          <div key={'5'}>
            <TimeText date={new Date(s.createdAt)} />
          </div>
        ]
      })}
      emptyPlaceholderText='No transactions yet'
    />
    {
      !isLoading && <Pagination
        curPage={curPage}
        numPages={Math.ceil(totalTransactions / pageSize)}
        onPageClick={page => setCurPage(page)}
      />
    }
    {
      exportTransactionsModalOpen &&
      <Modal visible={exportTransactionsModalOpen} onClose={() => setExportTransactionsModalOpen(false)} title='Export transactions'>
        <ExportTransactionsForm />
      </Modal>
    }
  </div>
})

export default Transactions
